<template>
  <v-container>
    <v-dialog v-model="dialog" width="1039px" persistent :retain-focus="false">
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="primary" class="white--text">
          <v-toolbar-title class="font-weight-bold">
            {{ $t('esgPlatform.selectSubOrganizations') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="black--text pa-6">
          <p class="mb-4">{{ $t('esgPlatform.pleaseSelectSubOrgsToProceed') }}</p>

          <div class="d-flex align-center mb-5">
            <div class="selected-badge">
              <span class="white--text font-weight-bold">{{ selectedRows.length }}</span>
            </div>
            <div class="title ml-3">{{ selectedRows.length === 1 ? $t('esgPlatform.subOrganizationSelected') : $t('esgPlatform.subOrganizationsSelected') }}</div>
            <v-spacer></v-spacer>
            <div class="py-1 title">{{ $t('esgPlatform.filterBy') }}</div>
            <v-btn class="mx-2" icon color="primary" @click="showFilters = true">
              <v-icon dark large>mdi-filter</v-icon>
            </v-btn>
          </div>

          <v-data-table
            v-model="selectedRows"
            :headers="headers"
            :items="organizations"
            :loading="loading"
            :loading-text="$t('esgPlatform.loadingSubOrganizations')"
            show-select
            item-key="id"
            hide-default-footer
            class="elevation-1"
          >
            <template v-slot:top>
              <div class="d-flex mt-5">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  :placeholder="$t('esgPlatform.searchSubOrganizations')"
                  dense
                  v-model="search"
                  @input="debouncedSearch"
                  class="shrink mr-5"
                  style="width: 306px"
                />
                <v-btn color="primary" small class="mt-1" @click="selectAllVisible">
                  {{ $t('esgPlatform.selectAllSubOrganizations') }}
                </v-btn>
                <v-spacer></v-spacer>
                <div class="py-3">{{ from }} - {{ to }} of {{ totalCount }}</div>
                <v-btn class="mx-2" fab small outlined color="primary" :disabled="page <= 1" @click="pageChange(-1)">
                  <v-icon dark>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn class="mx-2" fab small outlined color="primary" :disabled="page >= totalPages" @click="pageChange(1)">
                  <v-icon dark>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:item.membershipValidity="{ item }">
              <div class="d-flex align-center">
                <div :class="item.active ? 'green-box' : 'red-box'"></div>
                {{ formatDate(item.membershipValidity) }}
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <span :class="item.active ? 'green-text' : 'red-text'">
                {{ item.active ? $t('esgPlatform.active') : $t('esgPlatform.deactivated') }}
              </span>
            </template>
          </v-data-table>

          <v-divider class="mt-5"></v-divider>
          <v-card-actions class="mt-6 px-0">
            <v-spacer></v-spacer>
            <v-btn outlined class="mr-2" height="44" width="116" depressed color="primary" @click="closeModal">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn height="44" width="116" color="primary" :disabled="!selectedRows.length" @click="saveSelectedCooperatives">
              {{ $t('esgPlatform.continue') }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showFilters" width="356" content-class="esg-assessment-filter-dialog" transition="dialog-bottom-transition">
      <v-card min-height="100vh" rounded="0">
        <v-toolbar color="primary" class="white--text" flat :elevation="0">
          <v-toolbar-title class="font-weight-bold">{{ $t('esgPlatform.filterBy') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" @click="showFilters = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <label class="text-subtitle-1 font-weight-bold">{{ $t('status') }}</label>
          <v-select
            :items="statusOptions"
            item-text="label"
            item-value="value"
            outlined
            dense
            placeholder="Select"
            v-model="filter.status"
          />
          <v-row class="mt-5">
            <v-col cols="12" class="d-flex justify-space-around">
              <v-btn outlined width="130" color="primary" @click="clearFilters">{{ $t('esgPlatform.clearAll') }}</v-btn>
              <v-btn width="130" color="primary" @click="applyFilters">{{ $t('esgPlatform.save') }}</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import debounce from "lodash/debounce";
import moment from "moment";
import EsgService from "@/_services/EsgService";

export default {
  props: {
    dialogI: {
      type: Boolean,
      required: true,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    selectAll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      organizations: [],
      selectedRows: [],
      page: 1,
      limit: 5,
      totalCount: 0,
      totalPages: 1,
      search: "",
      loading: false,
      showFilters: false,
      filter: {
        status: "",
      },
      statusOptions: [
        { label: this.$t('esgPlatform.active'), value: "active" },
        { label: this.$t('esgPlatform.deactivated'), value: "deactivated" },
      ],
      headers: [
        { text: this.$t('esgPlatform.cooperative'), align: "start", value: "name" },
        { text: this.$t('esgPlatform.email'), align: "start", value: "email" },
        { text: this.$t('esgPlatform.membershipPlan'), align: "start", value: "membershipPlan" },
        { text: this.$t('esgPlatform.membershipValidity'), align: "start", value: "membershipValidity" },
        { text: this.$t('status'), align: "center", value: "status" },
      ],
    };
  },
  computed: {
    dialog() {
      return this.dialogI;
    },
    from() {
      return this.totalCount ? (this.page - 1) * this.limit + 1 : 0;
    },
    to() {
      return Math.min(this.page * this.limit, this.totalCount);
    },
  },
  mounted() {
    this.loadSubOrganizations();
  },
  methods: {
    async loadSubOrganizations() {
      this.loading = true;
      try {
        const response = await EsgService.getAssessmentSubOrganizations({
          page: this.page,
          limit: this.limit,
          search: this.search,
          ...this.filter,
        });
        this.organizations = response.data.rows || [];
        this.totalCount = response.data.count || 0;
        this.totalPages = response.data.totalPages || 1;
        const selectedIdSet = new Set(this.selectedIds);
        this.selectedRows = [
          ...this.selectedRows,
          ...this.organizations.filter((item) => selectedIdSet.has(item.id)),
        ].filter((item, index, arr) => arr.findIndex((selected) => selected.id === item.id) === index);
      } catch (err) {
        console.error(err);
        this.$notify({ title: this.$t('esgPlatform.failedToLoadSubOrganizations'), type: "error" });
      } finally {
        this.loading = false;
      }
    },
    debouncedSearch: debounce(function () {
      this.page = 1;
      this.loadSubOrganizations();
    }, 500),
    pageChange(direction) {
      this.page += direction;
      this.loadSubOrganizations();
    },
    selectAllVisible() {
      this.selectedRows = [
        ...this.selectedRows,
        ...this.organizations,
      ].filter((item, index, arr) => arr.findIndex((selected) => selected.id === item.id) === index);
    },
    clearFilters() {
      this.filter.status = "";
      this.page = 1;
      this.showFilters = false;
      this.loadSubOrganizations();
    },
    applyFilters() {
      this.page = 1;
      this.showFilters = false;
      this.loadSubOrganizations();
    },
    formatDate(date) {
      return date ? moment(date).format("YYYY-MM-DD") : "N/A";
    },
    closeModal() {
      this.$emit("closeDialog");
    },
    saveSelectedCooperatives() {
      this.$emit("cooperativesSelected", {
        selected: this.selectedRows,
        selectAll: this.selectAll,
        filter: this.filter,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.selected-badge {
  width: 31px;
  height: 31px;
  background: #00bd73;
  display: flex;
  border-radius: 20px;
  align-items: center;
  justify-content: center;
}

.green-text {
  color: #006400;
}

.red-text {
  color: #ff0000;
}

.red-box {
  background: #ff0000;
  border-radius: 2px;
  width: 17px;
  height: 16px;
  margin-right: 10px;
}

.green-box {
  border-radius: 2px;
  width: 17px;
  height: 16px;
  background: #006400;
  margin-right: 10px;
}

</style>

<style lang="scss">
.esg-assessment-filter-dialog {
  position: absolute;
  top: 0;
  min-height: 100%;
  right: 0;
  margin-right: 0;
  margin-top: 0;
}
</style>
